import { AxiosError } from 'axios'
import { z } from 'zod'
import { apiClient } from '@/lib/api/client.ts'
import { AUDIT_STATES, type AuditState } from '../types.ts'
import type { ApiResult, AuditsApi, AuditsApiError } from './audits-api.types.ts'

/**
 * Mirrors `services/engine/src/api/routes/audits.ts`:
 *
 *   GET   /api/audits                              → { items: AuditSummary[] }
 *   POST  /api/audits                              → { audit, toml, coverage } (201)
 *   GET   /api/audits/:id                          → AuditDescription
 *   GET   /api/audits/:id/stages                   → { items: StageSummary[] }
 *   POST  /api/audits/:id/stages/:stage/run        → { job } (202) | 409
 *   PATCH /api/audits/:id/quota                    → { audit, quota }
 *   GET   /api/audits/:id/review?state=open        → { items }
 *   GET   /api/pipelines                           → { items }
 *   GET   /api/pipelines/:id/backfill              → { job, backfilledFrom, backfilledTo }
 *
 * Zod at the boundary, dates coerced back, and every failure an envelope.
 */
const quotaSchema = z.object({
  asr_minutes: z.number().catch(0),
  llm_tokens: z.number().catch(0),
  amount: z.number().catch(0),
  currency: z.string().catch('EUR'),
})

const spendSchema = z.object({
  spent: z.object({
    asr_minutes: z.number().catch(0),
    llm_tokens: z.number().catch(0),
    amount: z.number().catch(0),
    calls: z.number().catch(0),
  }),
  quota: quotaSchema,
  exhausted: z.enum(['asr_minutes', 'llm_tokens', 'amount']).nullable().catch(null),
  units: z.number().catch(0),
  perUnit: z.number().catch(0),
})

const stateSchema = z
  .string()
  .transform((value) =>
    AUDIT_STATES.includes(value as AuditState) ? (value as AuditState) : 'failed',
  )

const summarySchema = z.object({
  id: z.string(),
  title: z.string(),
  pipelineIds: z.array(z.string()),
  periodFrom: z.coerce.date(),
  periodTo: z.coerce.date(),
  cutoffAt: z.coerce.date(),
  snapshotAt: z.coerce.date().nullable(),
  state: stateSchema,
  quota: quotaSchema.catch({ asr_minutes: 0, llm_tokens: 0, amount: 0, currency: 'EUR' }),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  spend: spendSchema.nullable().catch(null),
  anomalies: z.number().nullable().catch(null),
})

const detailSchema = z.object({
  audit: summarySchema,
  toml: z.object({
    stages: z.object({ enabled: z.array(z.string()) }),
    providers: z.object({
      asr: z.string().nullable(),
      llm: z.string().nullable(),
      llm_model: z.string().nullable(),
    }),
  }),
  counters: z.object({
    cardsPerPipeline: z.record(z.number()),
    calls: z.object({ withAudio: z.number(), withoutAudio: z.number() }),
    contacts: z.object({ withPhone: z.number(), withoutPhone: z.number() }),
  }),
  coverage: z.object({
    covered: z.boolean(),
    missing: z.array(
      z.object({ pipelineId: z.string(), from: z.coerce.date(), to: z.coerce.date() }),
    ),
  }),
  spend: spendSchema,
  blockedBy: z.array(z.string()).catch([]),
  log: z
    .array(
      z.object({
        id: z.string(),
        actor: z.string(),
        action: z.string(),
        subject: z.string().nullable(),
        details: z.record(z.unknown()).catch({}),
        at: z.coerce.date(),
      }),
    )
    .catch([]),
})

const UNIT_STATES = ['queued', 'running', 'done', 'failed', 'skipped', 'needs_review'] as const

const stageSummarySchema = z.object({
  stage: z.string(),
  level: z.enum(['audit', 'card']),
  enabled: z.boolean(),
  job: z.object({ state: z.string(), updatedAt: z.coerce.date() }).nullable(),
  units: z
    .object({ total: z.number() })
    .extend(
      Object.fromEntries(UNIT_STATES.map((s) => [s, z.number().catch(0)])) as Record<
        (typeof UNIT_STATES)[number],
        z.ZodCatch<z.ZodNumber>
      >,
    ),
  skipped: z.record(z.number()).catch({}),
})

const reviewSchema = z.object({
  id: z.string(),
  kind: z.string(),
  state: z.enum(['open', 'resolved']),
  leadId: z.string().nullable(),
})

const pipelineOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['deal', 'interaction']).catch('deal'),
})

const evidenceRefSchema = z.object({
  id: z.string(),
  kind: z.string(),
  refId: z.string(),
  startMs: z.number().nullable(),
  endMs: z.number().nullable(),
  quote: z.string().nullable(),
})

const cardSchema = z.object({
  card: z.object({
    id: z.string(),
    pipelineId: z.string(),
    pipelineName: z.string(),
    kind: z.enum(['deal', 'interaction']),
    name: z.string(),
    statusName: z.string().nullable(),
    lifecyclePhase: z.string().nullable(),
    assignedManagerId: z.string().nullable(),
    funnel: z.string().nullable(),
    tags: z.array(z.string()).catch([]),
    createdAt: z.coerce.date(),
    ludusLeadId: z.string().nullable(),
    link: z.object({
      confidence: z.string().nullable(),
      method: z.string().nullable(),
      linkedBy: z.string().nullable(),
    }),
  }),
  stages: z.array(
    z.object({ stage: z.string(), state: z.string(), reason: z.string().nullable() }),
  ),
  transcript: z
    .object({
      id: z.string(),
      language: z.string(),
      durationMs: z.number().nullable(),
      roleConfidence: z.number().nullable(),
      segments: z.array(
        z.object({
          idx: z.number(),
          startMs: z.number(),
          endMs: z.number(),
          speaker: z.string().nullable(),
          role: z.string().nullable(),
          text: z.string(),
        }),
      ),
    })
    .nullable(),
  audio: z.array(
    z.object({
      id: z.string(),
      mime: z.string().nullable(),
      durationMs: z.number().nullable(),
      url: z.string().nullable(),
      erasedAt: z.string().nullable(),
    }),
  ),
  facts: z.array(
    z.object({
      id: z.string(),
      key: z.string(),
      value: z.string(),
      kind: z.string(),
      confidence: z.number().nullable(),
      stage: z.string(),
      reviewState: z.string(),
      evidence: z.array(evidenceRefSchema),
    }),
  ),
  qa: z.array(
    z.object({
      phase: z.string(),
      applicability: z.string(),
      coverage: z.string(),
      fidelityScore: z.number().nullable(),
      explanation: z.string(),
    }),
  ),
  stageEvidence: z.array(
    z.object({
      id: z.string(),
      statusName: z.string().nullable(),
      occurredAt: z.coerce.date(),
      verdict: z.string(),
      explanation: z.string().nullable(),
      missing: z.string().nullable(),
    }),
  ),
  identity: z.object({
    cycle: z
      .object({
        id: z.string(),
        origin: z.string(),
        productKey: z.string().nullable(),
        anomalyFlags: z.array(z.string()).catch([]),
      })
      .nullable(),
    customer: z.object({ id: z.string(), anomaly: z.string() }).nullable(),
    siblings: z.array(
      z.object({
        id: z.string(),
        kind: z.enum(['deal', 'interaction']),
        name: z.string(),
        createdAt: z.coerce.date(),
        linkConfidence: z.string().nullable(),
      }),
    ),
  }),
  history: z.array(
    z.object({
      id: z.string(),
      at: z.coerce.date(),
      from: z.string().nullable(),
      to: z.string().nullable(),
      kind: z.string(),
    }),
  ),
})

const reviewRowSchema = z.object({
  id: z.string(),
  kind: z.string(),
  subjectType: z.string(),
  subjectId: z.string(),
  leadId: z.string().nullable(),
  proposal: z.record(z.unknown()).catch({}),
  evidence: z.record(z.unknown()).catch({}),
  state: z.enum(['open', 'resolved']),
  resolution: z.string().nullable().catch(null),
  resolvedBy: z.string().nullable().catch(null),
  reason: z.string().nullable().catch(null),
  ruleVersion: z.string(),
  createdAt: z.coerce.date(),
  confidence: z.number().nullable().catch(null),
})

const budgetSchema = z.object({
  hours: z.number().catch(0),
  spentHours: z.number().catch(0),
  decisions: z.number().catch(0),
})

const chatMessageSchema = z.object({
  id: z.string(),
  role: z.enum(['user', 'assistant']),
  text: z.string(),
  evidence: z
    .array(
      z.object({
        kind: z.string(),
        ref_id: z.string(),
        start_ms: z.number().optional(),
        end_ms: z.number().optional(),
      }),
    )
    .catch([]),
  inReplyTo: z.string().nullable(),
  polledAt: z.coerce.date().nullable(),
  answeredAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
})

const brainSchema = z.object({
  online: z.boolean(),
  lastPollAt: z.coerce.date().nullable(),
  polls: z.number().catch(0),
})

const backfillSchema = z.object({
  job: z
    .object({
      state: z.string(),
      pages: z.number().catch(0),
      error: z.string().nullable().catch(null),
    })
    .nullable(),
  backfilledFrom: z.coerce.date().nullable(),
  backfilledTo: z.coerce.date().nullable(),
})

function classify(err: unknown): AuditsApiError {
  if (err instanceof AxiosError) {
    const status = err.response?.status ?? null
    const body = err.response?.data as { error?: string; state?: string } | undefined
    const code = typeof body?.error === 'string' ? body.error : undefined
    if (status === 401) return { kind: 'unauthorized', status, message: 'unauthorized', code }
    if (status === 404) return { kind: 'not_found', status, message: code ?? 'not found', code }
    if (status === 409) return { kind: 'conflict', status, message: code ?? 'conflict', code }
    if (status === 400) return { kind: 'invalid', status, message: code ?? 'invalid', code }
    if (status === null) return { kind: 'network', status, message: err.message }
    return { kind: 'unknown', status, message: code ?? err.message, code }
  }
  return {
    kind: 'unknown',
    status: null,
    message: err instanceof Error ? err.message : String(err),
  }
}

async function attempt<T>(run: () => Promise<T>): Promise<ApiResult<T>> {
  try {
    return { success: true, data: await run() }
  } catch (err) {
    return { success: false, error: classify(err) }
  }
}

export const auditsApiProd: AuditsApi = {
  list: () =>
    attempt(async () => {
      const { data } = await apiClient.get('/audits')
      return z.object({ items: z.array(summarySchema) }).parse(data).items
    }),
  get: (auditId) =>
    attempt(async () => {
      const { data } = await apiClient.get(`/audits/${auditId}`)
      const parsed = detailSchema.parse(data)
      return {
        audit: parsed.audit,
        stagesEnabled: parsed.toml.stages.enabled,
        providers: parsed.toml.providers,
        counters: parsed.counters,
        coverage: parsed.coverage,
        spend: parsed.spend,
        blockedBy: parsed.blockedBy,
        log: parsed.log,
      }
    }),
  create: (input) =>
    attempt(async () => {
      const { data } = await apiClient.post('/audits', {
        title: input.title,
        pipelineIds: input.pipelineIds,
        periodFrom: input.periodFrom.toISOString(),
        periodTo: input.periodTo.toISOString(),
        ...(input.cutoffAt ? { cutoffAt: input.cutoffAt.toISOString() } : {}),
        ...(input.stages ? { stages: input.stages } : {}),
        ...(input.quota ? { quota: input.quota } : {}),
        ...(input.providers ? { providers: input.providers } : {}),
      })
      return z.object({ audit: summarySchema }).parse(data).audit
    }),
  stages: (auditId) =>
    attempt(async () => {
      const { data } = await apiClient.get(`/audits/${auditId}/stages`)
      return z.object({ items: z.array(stageSummarySchema) }).parse(data).items
    }),
  runStage: (auditId, stage, request) =>
    attempt(async () => {
      const { data } = await apiClient.post(`/audits/${auditId}/stages/${stage}/run`, {
        scope: request.scope,
        ...(request.ids ? { ids: request.ids } : {}),
        ...(request.limit === undefined ? {} : { limit: request.limit }),
      })
      return { jobId: z.object({ job: z.object({ id: z.string() }) }).parse(data).job.id }
    }),
  patchQuota: (auditId, patch) =>
    attempt(async () => {
      const { data } = await apiClient.patch(`/audits/${auditId}/quota`, patch)
      return z.object({ audit: summarySchema }).parse(data).audit
    }),
  openReviews: (auditId) =>
    attempt(async () => {
      const { data } = await apiClient.get(`/audits/${auditId}/review`, {
        params: { state: 'open' },
      })
      return z.object({ items: z.array(reviewSchema) }).parse(data).items
    }),
  pipelines: () =>
    attempt(async () => {
      const { data } = await apiClient.get('/pipelines')
      return z.object({ items: z.array(pipelineOptionSchema) }).parse(data).items
    }),
  backfill: (pipelineId) =>
    attempt(async () => {
      const { data } = await apiClient.get(`/pipelines/${pipelineId}/backfill`)
      return backfillSchema.parse(data)
    }),
  card: (auditId, cardId) =>
    attempt(async () => {
      const { data } = await apiClient.get(`/audits/${auditId}/cards/${cardId}`)
      return cardSchema.parse(data)
    }),
  reviewQueue: (auditId) =>
    attempt(async () => {
      const { data } = await apiClient.get(`/audits/${auditId}/review`)
      return z.object({ items: z.array(reviewRowSchema), budget: budgetSchema }).parse(data)
    }),
  decide: (auditId, queueId, input) =>
    attempt(async () => {
      const { data } = await apiClient.post(`/audits/${auditId}/review/${queueId}/decision`, {
        decision: input.decision,
        reason: input.reason,
      })
      return z.object({ row: reviewRowSchema.omit({ confidence: true }) }).parse(data)
        .row as unknown as import('../types.ts').ReviewRow
    }),
  chat: (auditId) =>
    attempt(async () => {
      const { data } = await apiClient.get(`/audits/${auditId}/chat`)
      return z.object({ messages: z.array(chatMessageSchema) }).parse(data).messages
    }),
  ask: (auditId, text) =>
    attempt(async () => {
      const { data } = await apiClient.post(`/audits/${auditId}/chat`, { text })
      return z.object({ message: chatMessageSchema }).parse(data).message
    }),
  brain: (auditId) =>
    attempt(async () => {
      const { data } = await apiClient.get(`/audits/${auditId}/brain`)
      return brainSchema.parse(data)
    }),
}
